import React from 'react';
import { Link } from 'react-router-dom';
import { Job } from '../types/job';
import { useAuth } from '../context/AuthContext';

interface LatestJobsSectionProps {
  jobs: Job[];
}

function formatBudget(min: number, max: number): string | null {
  if (min <= 0 && max <= 0) return null;
  if (min > 0 && max > 0) return `R${min.toLocaleString()} – R${max.toLocaleString()}`;
  if (min > 0) return `From R${min.toLocaleString()}`;
  return `Up to R${max.toLocaleString()}`;
}

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(timestamp).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
}

const LatestJobsSection: React.FC<LatestJobsSectionProps> = ({ jobs }) => {
  const { currentUser } = useAuth();

  if (jobs.length === 0) return null;

  const isContractor = currentUser?.isContractor ?? false;
  const applyTo = (jobId: number) => (isContractor ? `/jobs/${jobId}` : '/register');
  const applyLabel = isContractor ? 'View Job' : 'Apply';

  return (
    <section className="px-container-margin py-10 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-headline-sm text-headline-sm-mobile md:font-headline-md md:text-headline-md text-on-surface">
          Latest Jobs
        </h2>
        <Link
          to="/jobs"
          className="text-sm font-label-bold text-secondary hover:underline flex items-center gap-1"
        >
          View all
          <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {jobs.map(job => {
          const budget = formatBudget(job.budgetMin, job.budgetMax);
          return (
            <div
              key={job.id}
              className="bg-surface rounded-2xl border border-outline-variant/30 shadow-sm flex flex-col overflow-hidden"
            >
              {/* Thumbnail */}
              <div className="w-full h-36 bg-surface-container-low flex-shrink-0">
                {job.pictureUrl ? (
                  <img src={job.pictureUrl} alt={job.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="material-symbols-outlined text-outline-variant text-[40px]">handyman</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4 flex flex-col flex-1 gap-2">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-label-bold text-on-surface line-clamp-2 leading-snug">{job.title}</h3>
                  <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-label-bold bg-green-100 text-green-800">
                    Open
                  </span>
                </div>

                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-on-surface-variant">
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">location_on</span>
                    {job.location}
                  </span>
                  {job.categoryName && (
                    <span className="flex items-center gap-1">
                      <span className="material-symbols-outlined text-[13px]">category</span>
                      {job.categoryName}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">schedule</span>
                    {timeAgo(job.createdAt)}
                  </span>
                </div>

                {budget && (
                  <p className="text-sm font-label-bold text-primary flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">payments</span>
                    {budget}
                  </p>
                )}

                {/* Actions */}
                <div className="mt-auto pt-2 flex gap-2">
                  <Link
                    to={`/jobs/${job.id}`}
                    className="flex-1 text-center py-2 rounded-xl border border-outline-variant text-on-surface text-sm font-label-bold hover:bg-surface-container transition-colors"
                  >
                    Details
                  </Link>
                  <Link
                    to={applyTo(job.id)}
                    className="flex-1 text-center py-2 rounded-xl bg-primary text-on-primary text-sm font-label-bold hover:bg-primary/90 transition-colors"
                  >
                    {applyLabel}
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default LatestJobsSection;
